import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { MANAGER_SESSION_EXPIRED_EVENT, MANAGER_TOKEN_KEY } from "@/lib/authSession";

const AuthContext = createContext(null);

const TOKEN_KEY = MANAGER_TOKEN_KEY;

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  useEffect(() => {
    const syncToken = () => setToken(localStorage.getItem(TOKEN_KEY));
    const handleStorage = (event) => {
      if (event.key === TOKEN_KEY) syncToken();
    };

    window.addEventListener(MANAGER_SESSION_EXPIRED_EVENT, syncToken);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener(MANAGER_SESSION_EXPIRED_EVENT, syncToken);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const login = useCallback((newToken) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  const value = useMemo(
    () => ({
      token,
      isAuthenticated: !!token,
      login,
      logout,
    }),
    [token, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
